import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../App";
import { useUnsavedGuard } from "../lib/unsavedGuard";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Calendar, Clock, Check, Save, AlertTriangle, Edit2, ArrowLeftRight, CheckSquare, Square, Users, Eye
} from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function formatWeekRange(weekStart) {
  const start = new Date(weekStart + 'T12:00:00');
  const end = new Date(weekStart + 'T12:00:00');
  end.setDate(end.getDate() + 6);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function formatTime(time) {
  if (!time) return "";
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  return `${h % 12 || 12}:${minutes}${h >= 12 ? 'p' : 'a'}`;
}

// ============ EMPLOYEE AVAILABILITY VIEW ============
const EmployeeSchedule = () => {
  const { tenant } = useAuth();
  const swapEnabled = tenant?.plan === "starter" || tenant?.plan === "pro";
  const [shifts, setShifts] = useState([]);
  const [mySchedule, setMySchedule] = useState(null);
  const [myAvailability, setMyAvailability] = useState({});
  const [employeeId, setEmployeeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [swapDialog, setSwapDialog] = useState({ open: false, day: "" });
  const [eligibleEmps, setEligibleEmps] = useState([]);
  const [swapLoading, setSwapLoading] = useState(false);
  const [pendingSwaps, setPendingSwaps] = useState([]);
  const [incomingSwaps, setIncomingSwaps] = useState([]);
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [dirty, setDirtyLocal] = useState(false);
  // Employees can only submit availability for a FUTURE week — past weeks
  // are frozen, and the current week is frozen on Sunday since the schedule
  // is expected to be published by then. UX-friendly indicator + disabled
  // save button reinforce the rule (backend also enforces).
  const currentWeekMonday = getMonday(new Date());
  const isPastWeek = weekStart < currentWeekMonday;
  const isCurrentWeek = weekStart === currentWeekMonday;
  const isSundayToday = new Date().getDay() === 0;
  const weekLocked = isPastWeek || (isCurrentWeek && isSundayToday);
  const weekStatusLabel = isPastWeek
    ? "Past Week — locked"
    : isCurrentWeek
    ? (isSundayToday ? "Current Week — locked (Sunday)" : "Current Week")
    : "Future Week";
  // Team Schedule (read-only) — populated only when the tenant has enabled
  // schedule_visibility. The backend returns 403 when off, which we treat as
  // "feature disabled" — no error toast, just hide the card.
  const [teamSchedule, setTeamSchedule] = useState(null);
  const [teamVisibility, setTeamVisibility] = useState("off");
  // Collapsible state for Team Schedule + My Availability — persisted to
  // localStorage so the employee's preference survives reloads. Default
  // both collapsed: Team Schedule is informational and adds vertical scroll;
  // Availability is the one box most employees only touch once a week.
  const [teamOpen, setTeamOpen] = useState(() => localStorage.getItem("emp_team_open") === "1");
  const [availOpen, setAvailOpen] = useState(() => localStorage.getItem("emp_avail_open") !== "0");
  useEffect(() => { localStorage.setItem("emp_team_open", teamOpen ? "1" : "0"); }, [teamOpen]);
  useEffect(() => { localStorage.setItem("emp_avail_open", availOpen ? "1" : "0"); }, [availOpen]);
  const { setDirty: setGuardDirty, pending, proceed: proceedNav, cancel: cancelNav } = useUnsavedGuard();

  const setDirty = (v) => { setDirtyLocal(v); setGuardDirty(v); };

  // Block browser tab close / refresh / external nav
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Clear guard on unmount
  useEffect(() => () => setGuardDirty(false), [setGuardDirty]);

  useEffect(() => { fetchData(); }, [weekStart]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [shiftsRes, schedRes, availRes, swapsRes, teamRes] = await Promise.all([
        apiClient.get('/me/shifts'),
        apiClient.get(`/me/schedule?week_start=${weekStart}`),
        apiClient.get(`/me/availability?week_start=${weekStart}`),
        apiClient.get(`/shift-swaps?week_start=${weekStart}`).catch(() => ({ data: { swaps: [] } })),
        // Team schedule is opt-in per tenant. 403 = feature off → silent.
        apiClient.get(`/me/team-schedule?week_start=${weekStart}`).catch((err) => {
          if (err?.response?.status === 403) return { data: { __off: true } };
          return { data: null };
        }),
      ]);
      setShifts(shiftsRes.data.shifts);
      setMySchedule(schedRes.data.schedules?.[0] || null);
      setEmployeeId(availRes.data.employee_id);
      const eid = availRes.data.employee_id;
      const allSwaps = swapsRes.data.swaps;
      setPendingSwaps(allSwaps.filter(s => s.requester_id === eid && s.status === "pending"));
      setIncomingSwaps(allSwaps.filter(s => s.target_id === eid && s.status === "pending"));
      if (teamRes.data && !teamRes.data.__off) {
        setTeamSchedule(teamRes.data);
        setTeamVisibility(teamRes.data.visibility || "off");
      } else {
        setTeamSchedule(null);
        setTeamVisibility("off");
      }
      // Initialize availability from saved data or empty
      const existingAvail = availRes.data.availability?.available_shifts || {};
      const init = {};
      DAYS.forEach(day => { init[day] = existingAvail[day] || []; });
      setMyAvailability(init);
      setDirty(false);
    } catch (error) {
      console.error("Failed to fetch schedule data:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailability = (day, shiftId) => {
    setMyAvailability(prev => {
      const dayShifts = prev[day] || [];
      const updated = dayShifts.includes(shiftId)
        ? dayShifts.filter(id => id !== shiftId)
        : [...dayShifts, shiftId];
      return { ...prev, [day]: updated };
    });
    setSaved(false);
    setDirty(true);
  };

  const toggleAllForDay = (day) => {
    setMyAvailability(prev => {
      const dayShifts = prev[day] || [];
      const allShiftIds = shifts.map(s => s.shift_id);
      const allSelected = allShiftIds.length > 0 && allShiftIds.every(id => dayShifts.includes(id));
      return { ...prev, [day]: allSelected ? [] : allShiftIds };
    });
    setSaved(false);
    setDirty(true);
  };

  const setAllAvailability = (selectAll) => {
    const allShiftIds = shifts.map(s => s.shift_id);
    const next = {};
    DAYS.forEach(day => { next[day] = selectAll ? [...allShiftIds] : []; });
    setMyAvailability(next);
    setSaved(false);
    setDirty(true);
  };

  const handleSaveAvailability = async () => {
    if (weekLocked) {
      toast.error(
        isPastWeek
          ? "You can't change availability for past weeks."
          : "Availability for this week is locked (Sunday). Please pick next week."
      );
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/availability', {
        week_start: weekStart,
        available_shifts: myAvailability
      });
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error("Failed to save availability:", error);
      toast.error(error.response?.data?.detail || "Failed to save availability");
    } finally {
      setSaving(false);
    }
  };

  const navigateWeek = (dir) => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'));
  };

  const getAssignment = (day) => mySchedule?.assignments?.[day];

  const openSwapDialog = async (day) => {
    setSwapDialog({ open: true, day });
    setSwapLoading(true);
    try {
      const res = await apiClient.get(`/shift-swaps/eligible?week_start=${weekStart}&day=${day}`);
      setEligibleEmps(res.data.employees);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load eligible employees");
    } finally {
      setSwapLoading(false);
    }
  };

  const requestSwap = async (targetId) => {
    try {
      await apiClient.post('/shift-swaps', { week_start: weekStart, day: swapDialog.day, target_employee_id: targetId });
      toast.success("Swap request sent!");
      setSwapDialog({ open: false, day: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to request swap");
    }
  };

  const respondSwap = async (swapId, action) => {
    try {
      await apiClient.put(`/shift-swaps/${swapId}/respond`, { action });
      toast.success(action === "accept" ? "Swap accepted!" : "Swap declined");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to respond");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="employee-schedule-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">My Schedule</h1>
            <p className="text-slate-600 mt-1">View your shifts and submit availability</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} data-testid="prev-week-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-[#0A192F]">{formatWeekRange(weekStart)}</span>
              <span
                className={`ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  isPastWeek
                    ? "bg-slate-200 text-slate-500"
                    : isCurrentWeek
                    ? (isSundayToday ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700")
                    : "bg-emerald-100 text-emerald-700"
                }`}
                data-testid="week-status-pill"
              >
                {weekStatusLabel}
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} data-testid="next-week-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* My Assigned Schedule */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#0D9488]" /> My Assigned Shifts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {DAYS.map(day => {
                const assignment = getAssignment(day);
                return (
                  <div key={day} className="text-center">
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">{day.substring(0, 3)}</p>
                    {assignment ? (
                      <div className="bg-[#0D9488] text-white rounded-lg p-2" data-testid={`my-shift-${day}`}>
                        <p className="text-xs font-semibold">{assignment.shift_name || 'Shift'}</p>
                        <p className="text-[10px] opacity-80">{formatTime(assignment.start)} - {formatTime(assignment.end)}</p>
                        {swapEnabled && (
                          <button onClick={() => openSwapDialog(day)} className="mt-1 text-[10px] bg-white/20 hover:bg-white/30 rounded px-2 py-0.5 transition-colors" data-testid={`swap-btn-${day}`}>
                            <ArrowLeftRight className="w-3 h-3 inline mr-0.5" />Swap
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-2 text-slate-400 text-xs">
                        Off
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Team Schedule (read-only) — only when tenant has enabled
            schedule_visibility. Shows coworkers' shifts for the same week. */}
        {teamSchedule && teamSchedule.employees && teamSchedule.employees.length > 0 && (
          <Card className="border border-slate-200" data-testid="team-schedule-card">
            <CardHeader
              className="cursor-pointer select-none"
              onClick={() => setTeamOpen(v => !v)}
              data-testid="team-schedule-toggle"
            >
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-[#0D9488]" /> Team Schedule
                <span className="text-xs font-normal text-slate-500 ml-2">
                  ({teamVisibility === "all" ? "All employees" : "Same schedule group"} · read-only)
                </span>
                <span className="ml-auto text-slate-400 hover:text-slate-600">
                  {teamOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </span>
              </CardTitle>
            </CardHeader>
            {teamOpen && (
              <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="team-schedule-grid">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 px-3 text-left">Employee</th>
                      {DAYS.map(day => (
                        <th key={day} className="py-2 px-2 text-center capitalize font-semibold">{day.slice(0, 3)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {teamSchedule.employees.map(e => (
                      <tr key={e.employee_id} className="border-b border-slate-100 last:border-0">
                        <td className="py-2 px-3 font-medium text-[#0A192F]">{e.name}</td>
                        {DAYS.map(day => {
                          const slot = e.days?.[day];
                          if (!slot) {
                            return (
                              <td key={day} className="py-2 px-2 text-center">
                                <span className="inline-block px-2 py-1 rounded text-[11px] text-slate-400 bg-slate-50 border border-dashed border-slate-200">Off</span>
                              </td>
                            );
                          }
                          return (
                            <td key={day} className="py-2 px-2 text-center">
                              <div
                                className="inline-block px-2 py-1 rounded text-[11px] font-medium leading-tight"
                                style={{
                                  backgroundColor: slot.color ? `${slot.color}1A` : "#0D94881A",
                                  color: slot.color || "#0D9488",
                                  border: `1px solid ${slot.color || "#0D9488"}40`,
                                }}
                                title={`${slot.shift_name} · ${formatTime(slot.start_time)}–${formatTime(slot.end_time)}`}
                              >
                                <div className="font-semibold">{slot.shift_name}</div>
                                <div className="opacity-80">{formatTime(slot.start_time)}–{formatTime(slot.end_time)}</div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
            )}
          </Card>
        )}

        {/* Availability Submission */}
        <Card className="border border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between cursor-pointer select-none" onClick={() => setAvailOpen(v => !v)} data-testid="availability-toggle">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#0D9488]" /> My Availability
              <span className="ml-2 text-slate-400">
                {availOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </span>
            </CardTitle>
            <Button
              onClick={(e) => { e.stopPropagation(); handleSaveAvailability(); }}
              disabled={saving || (!dirty && !saved) || weekLocked}
              className={saved ? "bg-[#10B981] hover:bg-[#10B981]" : "bg-[#0D9488] hover:bg-[#14B8A6] disabled:opacity-50"}
              data-testid="save-availability-btn"
              title={weekLocked ? weekStatusLabel : undefined}
            >
              {saving ? "Saving..." : saved ? <><Check className="w-4 h-4 mr-2" /> Saved!</> : <><Save className="w-4 h-4 mr-2" /> {dirty ? "Save Changes" : "Save Availability"}</>}
            </Button>
          </CardHeader>
          {availOpen && (
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <p className="text-sm text-slate-500">Check the shifts you're available to work for each day this week.</p>
              {shifts.length > 0 && (() => {
                const totalCells = shifts.length * DAYS.length;
                const checkedCells = DAYS.reduce((sum, d) => sum + (myAvailability[d] || []).length, 0);
                const allSelected = totalCells > 0 && checkedCells === totalCells;
                return (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAllAvailability(!allSelected)}
                    className="self-start sm:self-auto"
                    data-testid="avail-select-all-btn"
                  >
                    {allSelected ? <><Square className="w-4 h-4 mr-1.5" /> Clear All</> : <><CheckSquare className="w-4 h-4 mr-1.5" /> Select All</>}
                  </Button>
                );
              })()}
            </div>
            {shifts.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No shifts defined yet. Ask your manager to set up shifts.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">Shift</th>
                      {DAYS.map(day => {
                        const dayShifts = myAvailability[day] || [];
                        const allDaySelected = shifts.length > 0 && shifts.every(s => dayShifts.includes(s.shift_id));
                        return (
                          <th key={day} className="px-2 py-2 text-center text-xs uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
                            <div className="flex flex-col items-center gap-1">
                              <span>{day.substring(0, 3)}</span>
                              <button
                                type="button"
                                onClick={() => toggleAllForDay(day)}
                                className={`text-[10px] normal-case font-medium px-1.5 py-0.5 rounded transition-colors ${allDaySelected ? "text-[#0D9488] hover:bg-teal-50" : "text-slate-500 hover:bg-slate-100"}`}
                                data-testid={`avail-toggle-day-${day}`}
                              >
                                {allDaySelected ? "Clear" : "All"}
                              </button>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map(shift => (
                      <tr key={shift.shift_id} className="hover:bg-slate-50/50">
                        <td className="px-3 py-3 border-b border-slate-100">
                          <div>
                            <p className="font-medium text-[#0A192F] text-sm">{shift.shift_name}</p>
                            <p className="text-xs text-slate-500">{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</p>
                          </div>
                        </td>
                        {DAYS.map(day => {
                          const isChecked = (myAvailability[day] || []).includes(shift.shift_id);
                          return (
                            <td key={day} className="px-2 py-3 border-b border-slate-100 text-center">
                              <div className="flex justify-center">
                                <Checkbox
                                  checked={isChecked}
                                  onCheckedChange={() => toggleAvailability(day, shift.shift_id)}
                                  className={isChecked ? "border-[#0D9488] data-[state=checked]:bg-[#0D9488]" : ""}
                                  data-testid={`avail-${shift.shift_id}-${day}`}
                                />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
          )}
        </Card>

        {/* Incoming Swap Requests */}
        {incomingSwaps.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/30" data-testid="incoming-swaps">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ArrowLeftRight className="w-4 h-4 text-amber-600" /> Swap Requests ({incomingSwaps.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {incomingSwaps.map(s => (
                <div key={s.swap_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100">
                  <div>
                    <p className="text-sm font-medium text-[#0A192F]">{s.requester_name} wants to swap <span className="font-semibold text-[#0D9488]">{s.day}</span></p>
                    <p className="text-xs text-slate-500">Week of {s.week_start}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => respondSwap(s.swap_id, "decline")} data-testid={`decline-swap-${s.swap_id}`}>Decline</Button>
                    <Button size="sm" className="bg-[#10B981] hover:bg-[#059669]" onClick={() => respondSwap(s.swap_id, "accept")} data-testid={`accept-swap-${s.swap_id}`}>Accept</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Pending Outgoing Swaps */}
        {pendingSwaps.length > 0 && (
          <Card className="border-slate-200" data-testid="pending-swaps">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-slate-500">
                <Clock className="w-4 h-4" /> Your Pending Swap Requests
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {pendingSwaps.map(s => (
                <div key={s.swap_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span><span className="font-medium">{s.day}</span> swap with {s.target_name}</span>
                  <Badge variant="outline" className="text-amber-600">Pending</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Swap Dialog */}
      <Dialog open={swapDialog.open} onOpenChange={(open) => !open && setSwapDialog({ open: false, day: "" })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-[#0D9488]" /> Swap {swapDialog.day} Shift
            </DialogTitle>
            <DialogDescription>Choose who to request a swap with</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto py-2">
            {swapLoading ? (
              <div className="text-center py-4 text-slate-400">Loading eligible employees...</div>
            ) : eligibleEmps.length === 0 ? (
              <div className="text-center py-4 text-slate-400">No eligible employees found</div>
            ) : (
              eligibleEmps.map(emp => (
                <div key={emp.employee_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-[#0D9488] transition-colors" data-testid={`swap-target-${emp.employee_id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#0A192F]">{emp.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {emp.has_availability ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Available</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400 text-[10px]">No availability</Badge>
                      )}
                      {emp.currently_assigned && (
                        <span className="text-[10px] text-slate-400">Has shift assigned</span>
                      )}
                    </div>
                  </div>
                  <Button size="sm" className="bg-[#0D9488] hover:bg-[#14B8A6]" onClick={() => requestSwap(emp.employee_id)}>Request</Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Guard */}
      <Dialog open={pending !== null} onOpenChange={(open) => { if (!open) cancelNav(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="unsaved-changes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Unsaved Changes
            </DialogTitle>
            <DialogDescription>You have unsaved availability changes. Save before leaving, or your changes will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" onClick={cancelNav} data-testid="unsaved-cancel-btn">Stay on Page</Button>
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => { setDirtyLocal(false); proceedNav(); }} data-testid="unsaved-discard-btn">
              Discard & Leave
            </Button>
            <Button
              className="bg-[#0D9488] hover:bg-[#14B8A6]"
              disabled={saving}
              onClick={async () => { await handleSaveAvailability(); proceedNav(); }}
              data-testid="unsaved-save-btn"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save & Continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// ============ MANAGER SCHEDULE BOARD (CHECKBOX GRID) ============
const ManagerSchedule = () => {
  const { tenant } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [selectedGroup, setSelectedGroup] = useState("");
  // Local schedule state for instant UI updates
  const [localAssignments, setLocalAssignments] = useState({});
  const [editingCell, setEditingCell] = useState(null); // "empId-day" key for time override
  const [confirmAssign, setConfirmAssign] = useState(null); // { employeeId, day, shift, empName }
  const [pendingApprovalSwaps, setPendingApprovalSwaps] = useState([]);
  const { setDirty: setGuardDirty, pending: navPending, proceed: proceedNav, cancel: cancelNav } = useUnsavedGuard();

  // Sync hasChanges into global unsaved guard
  useEffect(() => { setGuardDirty(hasChanges); }, [hasChanges, setGuardDirty]);
  useEffect(() => () => setGuardDirty(false), [setGuardDirty]);

  // Block browser tab close / refresh
  useEffect(() => {
    if (!hasChanges) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasChanges]);

  useEffect(() => { fetchData(); }, [weekStart, selectedGroup]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, groupRes, shiftRes, schedRes, availRes, swapsRes] = await Promise.all([
        apiClient.get('/employees?status=active'),
        apiClient.get('/schedule-groups'),
        apiClient.get(`/shifts${selectedGroup ? `?group_id=${selectedGroup}` : ''}`),
        apiClient.get(`/schedules?week_start=${weekStart}`),
        apiClient.get(`/availability?week_start=${weekStart}`),
        apiClient.get(`/shift-swaps?week_start=${weekStart}&status=accepted`).catch(() => ({ data: { swaps: [] } }))
      ]);
      setEmployees(empRes.data.employees);
      setGroups(groupRes.data.schedule_groups);
      setShifts(shiftRes.data.shifts);
      setSchedules(schedRes.data.schedules);
      setAvailability(availRes.data.availability);
      setPendingApprovalSwaps(swapsRes.data.swaps);
      // Auto-select first group if none selected
      if (!selectedGroup && groupRes.data.schedule_groups.length > 0) {
        setSelectedGroup(groupRes.data.schedule_groups[0].group_id);
      }
      // Build local assignments map: {employee_id: {day: {shift_id, start, end}}}
      const localMap = {};
      empRes.data.employees.forEach(emp => {
        const sched = schedRes.data.schedules.find(s => s.employee_id === emp.employee_id);
        localMap[emp.employee_id] = sched?.assignments || {};
      });
      setLocalAssignments(localMap);
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (dir) => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'));
  };

  const isAvailable = (employeeId, day, shiftId) => {
    const avail = availability.find(a => a.employee_id === employeeId);
    if (!avail) return false; // No availability submitted = not available
    const dayShifts = avail.available_shifts?.[day];
    if (!dayShifts) return false; // No data for this day = not available
    return dayShifts.includes(shiftId);
  };

  const hasAnyAvailability = (employeeId, day) => {
    const avail = availability.find(a => a.employee_id === employeeId);
    if (!avail) return false;
    const dayShifts = avail.available_shifts?.[day];
    return dayShifts && dayShifts.length > 0;
  };

  const doAssign = (employeeId, day, shift) => {
    setLocalAssignments(prev => {
      const empAssignments = { ...prev[employeeId] };
      empAssignments[day] = {
        shift_id: shift.shift_id,
        shift_name: shift.shift_name,
        start: shift.start_time,
        end: shift.end_time
      };
      return { ...prev, [employeeId]: empAssignments };
    });
    setHasChanges(true);
  };

  const toggleShift = (employeeId, day, shift) => {
    const currentAssignment = (localAssignments[employeeId] || {})[day];
    const isCurrentlyAssigned = currentAssignment?.shift_id === shift.shift_id;

    // If unassigning, always allow
    if (isCurrentlyAssigned) {
      setLocalAssignments(prev => {
        const empAssignments = { ...prev[employeeId] };
        empAssignments[day] = null;
        setEditingCell(null);
        return { ...prev, [employeeId]: empAssignments };
      });
      setHasChanges(true);
      return;
    }

    // If assigning to an unavailable slot, show styled confirmation
    const shiftAvailable = isAvailable(employeeId, day, shift.shift_id);
    if (!shiftAvailable) {
      const empName = employees.find(e => e.employee_id === employeeId)?.name || 'Employee';
      setConfirmAssign({ employeeId, day, shift, empName });
      return;
    }

    doAssign(employeeId, day, shift);
  };

  const updateShiftTime = (employeeId, day, field, value) => {
    setLocalAssignments(prev => {
      const empAssignments = { ...prev[employeeId] };
      if (empAssignments[day]) {
        empAssignments[day] = { ...empAssignments[day], [field]: value };
      }
      return { ...prev, [employeeId]: empAssignments };
    });
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const empsToSave = filteredEmployees;
      const updates = empsToSave.map(emp => ({
        employee_id: emp.employee_id,
        assignments: localAssignments[emp.employee_id] || {}
      }));
      await apiClient.post('/schedules/bulk', { week_start: weekStart, updates });
      setHasChanges(false);
      fetchData();
    } catch (error) {
      console.error("Failed to save schedules:", error);
      toast.error("Failed to save schedules");
    } finally {
      setSaving(false);
    }
  };

  const handleApproveSwap = async (swapId, action) => {
    try {
      await apiClient.put(`/shift-swaps/${swapId}/approve`, { action });
      toast.success(action === "approve" ? "Swap approved & executed!" : "Swap rejected");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to process swap");
    }
  };

  // Filter employees by selected schedule group (include unassigned employees in all groups)
  // Then further filter to only show employees who submitted availability for this week
  const filteredEmployees = (() => {
    let emps = selectedGroup
      ? employees.filter(emp => !emp.schedule_group_id || emp.schedule_group_id === selectedGroup)
      : employees;
    // Only show employees who have availability for this week
    return emps.filter(emp => {
      const avail = availability.find(a => a.employee_id === emp.employee_id);
      if (!avail) return false; // No availability submitted — hide
      // Check if they have at least one available shift across the week
      const shifts_data = avail.available_shifts || {};
      const hasAnyShift = Object.values(shifts_data).some(dayShifts => dayShifts && dayShifts.length > 0);
      return hasAnyShift;
    });
  })();

  const calcWeeklyHours = (employeeId) => {
    const assignments = localAssignments[employeeId] || {};
    let total = 0;
    Object.values(assignments).forEach(a => {
      if (a && a.start && a.end) {
        const [sh, sm] = a.start.split(':').map(Number);
        const [eh, em] = a.end.split(':').map(Number);
        let gross = (eh + em / 60) - (sh + sm / 60);
        if (gross <= 0) gross += 24; // Handle overnight/midnight shifts
        // Subtract expected break hours for the assigned shift
        const shift = shifts.find(s => s.shift_id === a.shift_id);
        const breakHrs = shift?.expected_break_hours || 0;
        total += Math.max(0, gross - breakHrs);
      }
    });
    return Math.round(total * 10) / 10;
  };

  return (
    <Layout>
      <div data-testid="schedule-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight flex items-center gap-2">
              Schedule Board
              {tenant?.schedule_visibility && tenant.schedule_visibility !== "off" && (
                <span
                  data-testid="team-visibility-indicator"
                  title={`Employees can view this schedule (${tenant.schedule_visibility === "all" ? "all employees" : "same schedule group only"})`}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0D9488]/10 text-[#0D9488] cursor-help"
                >
                  <Eye className="w-4 h-4" />
                </span>
              )}
            </h1>
            <p className="text-slate-600 mt-1">Click shift badges to assign. Changes save in bulk.</p>
          </div>
          <div className="flex items-center gap-3">
            {groups.length > 0 && (
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-[#0A192F] bg-white"
                data-testid="schedule-group-select"
              >
                <option value="">All Shifts</option>
                {groups.map(g => (
                  <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} data-testid="prev-week-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-[#0A192F]">{formatWeekRange(weekStart)}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} data-testid="next-week-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <Button
              onClick={handleSaveAll}
              disabled={saving || !hasChanges}
              className={hasChanges ? "bg-[#0D9488] hover:bg-[#14B8A6]" : "bg-slate-300"}
              data-testid="save-schedule-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : hasChanges ? "Save All" : "Saved"}
            </Button>
          </div>
        </div>

        {/* Shift Legend */}
        <div className="flex flex-wrap items-center gap-3">
          {shifts.map((shift, i) => {
            const colors = ["bg-[#0D9488]", "bg-[#6366F1]", "bg-[#F59E0B]", "bg-[#EC4899]", "bg-[#8B5CF6]"];
            return (
              <div key={shift.shift_id} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${colors[i % colors.length]}`}></div>
                <span className="text-sm text-slate-600">{shift.shift_name} ({formatTime(shift.start_time)}-{formatTime(shift.end_time)})</span>
              </div>
            );
          })}
          <div className="flex items-center gap-2 ml-4">
            <div className="w-3 h-3 rounded-full bg-red-200 border border-red-300"></div>
            <span className="text-sm text-slate-500">Unavailable</span>
          </div>
        </div>

        {/* Schedule Grid */}
        <Card className="border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-semibold border-b border-r border-slate-200 w-[180px] min-w-[180px]">Employee</th>
                  {DAYS.map(day => (
                    <th key={day} className="px-2 py-3 text-center text-xs uppercase tracking-wider text-slate-500 font-semibold border-b border-r border-slate-200 min-w-[100px]">{day.substring(0, 3)}</th>
                  ))}
                  <th className="px-3 py-3 text-center text-xs uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200 w-[60px]">Hrs</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}><td colSpan={9} className="p-3"><div className="h-12 bg-slate-100 rounded animate-pulse"></div></td></tr>
                  ))
                ) : filteredEmployees.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-12 text-slate-500">{selectedGroup ? "No employees assigned to this schedule group." : "No active employees."}</td></tr>
                ) : (
                  filteredEmployees.map(emp => {
                    const empAssignments = localAssignments[emp.employee_id] || {};
                    return (
                      <tr key={emp.employee_id} className="hover:bg-slate-50/30 transition-colors">
                        <td className="px-4 py-2 border-b border-r border-slate-200">
                          <div className="flex items-center gap-2">
                            <Avatar className="w-7 h-7">
                              <AvatarImage src={emp.avatar} alt={emp.name} />
                              <AvatarFallback className="bg-[#0D9488] text-white text-xs">{emp.name.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-[#0A192F] text-sm truncate">{emp.name}</span>
                          </div>
                        </td>
                        {DAYS.map(day => {
                          const assignment = empAssignments[day];
                          const dayAvailable = hasAnyAvailability(emp.employee_id, day);
                          const colors = ["bg-[#0D9488]", "bg-[#6366F1]", "bg-[#F59E0B]", "bg-[#EC4899]", "bg-[#8B5CF6]"];
                          const cellKey = `${emp.employee_id}-${day}`;
                          const isEditing = editingCell === cellKey;

                          return (
                            <td
                              key={day}
                              className={`px-1 py-1.5 border-b border-r border-slate-200 ${!dayAvailable ? 'bg-red-50' : ''}`}
                              data-testid={`cell-${emp.employee_id}-${day}`}
                            >
                              <div className="flex flex-col gap-0.5">
                                {shifts.map((shift, i) => {
                                  const isAssigned = assignment?.shift_id === shift.shift_id;
                                  const shiftAvailable = isAvailable(emp.employee_id, day, shift.shift_id);
                                  const color = colors[i % colors.length];

                                  return (
                                    <button
                                      key={shift.shift_id}
                                      onClick={() => toggleShift(emp.employee_id, day, shift)}
                                      className={`
                                        w-full px-1 py-1 rounded text-[10px] font-medium transition-all duration-150 leading-tight
                                        ${isAssigned
                                          ? `${color} text-white shadow-sm`
                                          : shiftAvailable
                                            ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                            : 'bg-red-50 text-red-300 line-through'
                                        }
                                      `}
                                      title={`${shift.shift_name} ${formatTime(shift.start_time)}-${formatTime(shift.end_time)}${!shiftAvailable ? ' (unavailable)' : ''}`}
                                      data-testid={`toggle-${emp.employee_id}-${day}-${shift.shift_id}`}
                                    >
                                      {isAssigned && <Check className="w-3 h-3 inline mr-0.5" />}
                                      {shift.shift_name.substring(0, 4)}
                                    </button>
                                  );
                                })}
                                {/* Time override for assigned shift */}
                                {assignment && (
                                  isEditing ? (
                                    <div className="flex flex-col gap-0.5 mt-0.5" data-testid={`time-override-${cellKey}`}>
                                      <input
                                        type="time"
                                        value={assignment.start || ""}
                                        onChange={(e) => updateShiftTime(emp.employee_id, day, 'start', e.target.value)}
                                        className="w-full text-[10px] px-1 py-0.5 border border-slate-300 rounded bg-white"
                                        data-testid={`time-start-${cellKey}`}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <input
                                        type="time"
                                        value={assignment.end || ""}
                                        onChange={(e) => updateShiftTime(emp.employee_id, day, 'end', e.target.value)}
                                        className="w-full text-[10px] px-1 py-0.5 border border-slate-300 rounded bg-white"
                                        data-testid={`time-end-${cellKey}`}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setEditingCell(null); }}
                                        className="text-[9px] text-[#0D9488] font-semibold hover:underline"
                                      >Done</button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setEditingCell(cellKey); }}
                                      className="text-[9px] text-slate-400 hover:text-[#0D9488] mt-0.5 flex items-center justify-center gap-0.5"
                                      data-testid={`edit-time-${cellKey}`}
                                      title="Override shift time"
                                    >
                                      <Edit2 className="w-2.5 h-2.5" />
                                      {formatTime(assignment.start)}-{formatTime(assignment.end)}
                                    </button>
                                  )
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 border-b border-slate-200 text-center">
                          <span className="font-semibold text-[#0D9488] text-sm">{calcWeeklyHours(emp.employee_id)}h</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Unavailable Shift Confirmation Dialog */}
        <Dialog open={!!confirmAssign} onOpenChange={(open) => { if (!open) setConfirmAssign(null); }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-2">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
              <DialogTitle className="text-center text-[#0A192F]">Assign Unavailable Shift?</DialogTitle>
              <DialogDescription className="text-center text-slate-500 pt-2">
                <span className="font-semibold text-[#0A192F]">{confirmAssign?.empName}</span> marked{" "}
                <span className="font-semibold text-[#0A192F]">"{confirmAssign?.shift?.shift_name}"</span> on{" "}
                <span className="font-semibold text-[#0A192F]">{confirmAssign?.day}</span> as unavailable.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => setConfirmAssign(null)}
                data-testid="confirm-assign-cancel"
              >
                Cancel
              </Button>
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => {
                  doAssign(confirmAssign.employeeId, confirmAssign.day, confirmAssign.shift);
                  setConfirmAssign(null);
                }}
                data-testid="confirm-assign-ok"
              >
                Assign Anyway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Swap Approvals */}
      {pendingApprovalSwaps.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 pb-8">
          <Card className="border-amber-200 bg-amber-50/30" data-testid="swap-approvals">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ArrowLeftRight className="w-4 h-4 text-amber-600" /> Swap Requests Awaiting Approval ({pendingApprovalSwaps.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {pendingApprovalSwaps.map(s => (
                <div key={s.swap_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100">
                  <div>
                    <p className="text-sm font-medium text-[#0A192F]">
                      <span className="font-semibold">{s.requester_name}</span> ↔ <span className="font-semibold">{s.target_name}</span>
                    </p>
                    <p className="text-xs text-slate-500">{s.day} · Week of {s.week_start} · Both employees agreed</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleApproveSwap(s.swap_id, "reject")} data-testid={`reject-swap-${s.swap_id}`}>Reject</Button>
                    <Button size="sm" className="bg-[#10B981] hover:bg-[#059669]" onClick={() => handleApproveSwap(s.swap_id, "approve")} data-testid={`approve-swap-${s.swap_id}`}>Approve</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Unsaved Changes Guard (Manager) */}
      <Dialog open={navPending !== null} onOpenChange={(open) => { if (!open) cancelNav(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="manager-unsaved-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Unsaved Schedule Changes
            </DialogTitle>
            <DialogDescription>You have unsaved schedule assignments. Save before leaving, or your changes will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" onClick={cancelNav} data-testid="manager-unsaved-cancel">Stay on Page</Button>
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => { setHasChanges(false); proceedNav(); }} data-testid="manager-unsaved-discard">
              Discard & Leave
            </Button>
            <Button
              className="bg-[#0D9488] hover:bg-[#14B8A6]"
              disabled={saving}
              onClick={async () => { await handleSaveAll(); proceedNav(); }}
              data-testid="manager-unsaved-save"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save & Continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// ============ MAIN SCHEDULE (ROLE-BASED) ============
const Schedule = () => {
  const { user } = useAuth();
  return user?.role === "employee" ? <EmployeeSchedule /> : <ManagerSchedule />;
};

export default Schedule;
